import { GiftCardReplacementFields, giftReplacementInput } from "../cashier/GiftCardReplacementFields";
import { RefundGiftCardPrintPanel } from "../cashier/RefundGiftCardPrintPanel";
import { valueError } from "../shared/value-copy";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  ApprovalBadge,
  Button,
  Card,
  DataTable,
  Dialog,
  Grid,
  Inline,
  KpiCard,
  PageHeader,
  Panel,
  SelectField,
  Spinner,
  Stack,
  StatusBadge,
  TextAreaField,
  formatMoneyMinor,
  useToast
} from "@leylion/design-system";
import {
  decideApprovalRequest,
  executeApprovalRequest,
  getApprovalRequests
} from "../api/client";
import type { ApprovalRequestItem } from "../api/identity-types";
import type { ContextOption } from "../api/types";
import "./manager-approvals.css";

type Language = "ar" | "en";
type StatusFilter = "Pending" | "All" | "Approved" | "Rejected" | "Expired";

type OperationalApprovalPayload = {
  orderId: string;
  orderNumber: string;
  expectedOrderVersion: number;
  currency?: string;
  reason: string;
  originalTotalMinor?: number;
  requestedRefundTotalMinor?: number;
  orderTotalMinor?: number;
  serviceType?: string;
  orderStatus?: string;
  paymentStatus?: string;
  scheduledOrderId?: string;
  bookingNumber?: string;
  expectedScheduledOrderVersion?: number;
  previousFulfillmentAt?: string;
  fulfillmentAt?: string;
  previousPreparationLeadMinutes?: number;
  preparationLeadMinutes?: number;
  rescheduleCount?: number;
  depositPaidMinor?: number;
  balancePaidMinor?: number;
  scheduledOrderStatus?: string;
  kitchenReleaseState?: string;
  depositCapturedMinor?: number;
  balanceCapturedMinor?: number;
  disposition?: "NoPayment" | "RefundAll" | "ForfeitAll" | "PartialRefund";
  depositRefundMinor?: number | null;
  lines?: Array<{
    originalOrderLineId: string;
    sku: string;
    productNameAr: string;
    productNameEn: string;
    variantNameAr: string;
    variantNameEn: string;
    orderedQuantityMilli: number;
    previouslyRefundedQuantityMilli: number;
    quantityMilli: number;
    totalMinor: number;
  }>;
};

function readOperationalPayload(item: ApprovalRequestItem | null): OperationalApprovalPayload | null {
  if (!item) return null;

  try {
    const parsed = JSON.parse(item.payloadJson) as OperationalApprovalPayload;
    return parsed && typeof parsed.orderId === "string" ? parsed : null;
  } catch {
    return null;
  }
}

function isOperationalRequest(item: ApprovalRequestItem): boolean {
  return item.operation === "Refund"
    || item.operation === "VoidItem"
    || item.operation === "PreOrderReschedule"
    || item.operation === "PreOrderCancellation";
}

function isManagerApprovalGateError(error: unknown): boolean {
  const candidate = error as { status?: number; problem?: { title?: string; code?: string } };
  const code = candidate.problem?.title ?? candidate.problem?.code ?? "";
  return (candidate.status === 401 || candidate.status === 403)
    && code.startsWith("MANAGER_APPROVAL_PIN_");
}

export function ManagerApprovalsCenter({
  language,
  context,
  managerAccessToken,
  managerAccessExpiresAt,
  canSelfDecide,
  permissions,
  onGateInvalid
}: {
  language: Language;
  context: ContextOption;
  managerAccessToken: string;
  managerAccessExpiresAt: string;
  canSelfDecide: boolean;
  permissions: string[];
  onGateInvalid: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const canDecide = true;
  const [requests, setRequests] = useState<ApprovalRequestItem[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequestItem | null>(null);
  const [decisionReason, setDecisionReason] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("Pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [replacementFor, setReplacementFor] = useState<string | null>(null);
  const [noExpiry, setNoExpiry] = useState(false);
  const [expiry, setExpiry] = useState("");
  const canSetExpiry = permissions.includes("giftcards.manage");
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const loadRequests = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const items = (await getApprovalRequests(managerAccessToken)).filter(isOperationalRequest);
      items.sort((left, right) => {
        if (left.status === "Pending" && right.status !== "Pending") return -1;
        if (right.status === "Pending" && left.status !== "Pending") return 1;
        return new Date(right.requestedAt).getTime() - new Date(left.requestedAt).getTime();
      });
      setRequests(items);
      setSelectedRequest((current) =>
        current ? items.find((item) => item.id === current.id) ?? null : null
      );
      setLastUpdatedAt(new Date());
      return items;
    } catch (caught) {
      if (isManagerApprovalGateError(caught)) {
        onGateInvalid();
        return [] as ApprovalRequestItem[];
      }
      setError(caught instanceof Error ? caught.message : "Unable to load approval requests.");
      return [] as ApprovalRequestItem[];
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    void loadRequests(true);
    const timer = window.setInterval(() => void loadRequests(), 10_000);
    return () => window.clearInterval(timer);
  }, [managerAccessToken]);

  const filteredRequests = useMemo(
    () => requests.filter((item) => statusFilter === "All" || item.status === statusFilter),
    [requests, statusFilter]
  );

  const pendingCount = requests.filter((item) => item.status === "Pending").length;
  const executionFailedCount = requests.filter((item) => item.executionStatus === "Failed").length;
  const executedCount = requests.filter((item) => item.executionStatus === "Succeeded").length;
  const selectedPayload = readOperationalPayload(selectedRequest);

  const operationLabel = (item: ApprovalRequestItem) => {
    if (item.operation === "Refund") return ar ? "استرجاع" : "Refund";
    if (item.operation === "VoidItem") return ar ? "إهلاك / إلغاء إداري" : "Manager void";
    if (item.operation === "PreOrderReschedule") return ar ? "إعادة جدولة طلب مستقبلي" : "Future-order reschedule";
    return ar ? "إلغاء وتسوية طلب مستقبلي" : "Future-order cancellation";
  };

  const approvalDisplayReference = (payload: OperationalApprovalPayload | null) =>
    payload?.bookingNumber ?? payload?.orderNumber ?? "—";

  const approvalDisplayAmount = (
    item: ApprovalRequestItem,
    payload: OperationalApprovalPayload | null
  ): number | undefined => {
    if (!payload) return undefined;
    if (item.operation === "Refund") return payload.requestedRefundTotalMinor;
    if (item.operation === "VoidItem") return payload.orderTotalMinor;
    if (item.operation === "PreOrderReschedule") {
      return (payload.depositPaidMinor ?? 0) + (payload.balancePaidMinor ?? 0);
    }
    return (payload.depositCapturedMinor ?? 0) + (payload.balanceCapturedMinor ?? 0);
  };

  const cancellationRefundExposure = (payload: OperationalApprovalPayload | null): number => {
    if (!payload) return 0;
    const deposit = payload.depositCapturedMinor ?? 0;
    const balance = payload.balanceCapturedMinor ?? 0;
    const depositRefund = payload.disposition === "RefundAll"
      ? deposit
      : payload.disposition === "PartialRefund"
        ? payload.depositRefundMinor ?? 0
        : 0;
    return balance + depositRefund;
  };

  const cancellationDispositionLabel = (value: OperationalApprovalPayload["disposition"]) => {
    if (value === "RefundAll") return ar ? "استرداد العربون كاملاً" : "Refund full deposit";
    if (value === "PartialRefund") return ar ? "استرداد جزئي ومصادرة الباقي" : "Partial refund; forfeit remainder";
    if (value === "ForfeitAll") return ar ? "مصادرة العربون كاملاً" : "Forfeit full deposit";
    return ar ? "لا توجد دفعة" : "No payment";
  };

  const executionBadge = (item: ApprovalRequestItem) => {
    if (item.status === "Pending") {
      return <StatusBadge tone="warning">{ar ? "بانتظار المدير" : "Awaiting manager"}</StatusBadge>;
    }
    if (item.executionStatus === "Succeeded") {
      return <StatusBadge tone="success">{ar ? "تم التنفيذ" : "Executed"}</StatusBadge>;
    }
    if (item.executionStatus === "Failed") {
      return <StatusBadge tone="danger">{ar ? "فشل التنفيذ" : "Execution failed"}</StatusBadge>;
    }
    if (item.status === "Approved" && item.executionStatus === "Pending") {
      return <StatusBadge tone="info">{ar ? "جاهز للتنفيذ" : "Ready to execute"}</StatusBadge>;
    }
    return <StatusBadge tone="neutral">{ar ? "لا يوجد تنفيذ" : "No execution"}</StatusBadge>;
  };

  const formatMoney = (minor: number | undefined, currency = "AED") =>
    formatMoneyMinor(minor ?? 0, currency, ar ? "ar-AE" : "en-AE");

  const openRequest = (item: ApprovalRequestItem) => {
    setSelectedRequest(item);
    setDecisionReason(item.decisionReason ?? "");
    setError("");
  };

  const reject = async () => {
    if (!selectedRequest || !decisionReason.trim()) {
      setError(ar ? "سبب الرفض إلزامي." : "A rejection reason is required.");
      return;
    }

    try {
      setBusy(true);
      await decideApprovalRequest(selectedRequest, false, decisionReason.trim(), managerAccessToken);
      toast.notify({
        tone: "warning",
        title: ar ? "تم رفض الطلب" : "Request rejected"
      });
      setSelectedRequest(null);
      setDecisionReason("");
      await loadRequests();
    } catch (caught) {
      if (isManagerApprovalGateError(caught)) {
        onGateInvalid();
        return;
      }
      setError(caught instanceof Error ? caught.message : "Unable to reject request.");
    } finally {
      setBusy(false);
    }
  };

  const approveAndExecute = async () => {
    if (!selectedRequest) return;

    try {
      setBusy(true);
      const approved = await decideApprovalRequest(
        selectedRequest,
        true,
        decisionReason.trim() || (ar ? "تمت المراجعة والموافقة من المدير." : "Approved after manager review."),
        managerAccessToken
      );
      const executed = await executeApprovalRequest(approved, managerAccessToken);
      setSelectedRequest(executed);
      toast.notify({
        tone: "success",
        title: ar ? "تمت الموافقة والتنفيذ" : "Approved and executed"
      });
      await loadRequests();
    } catch (caught) {
      if (isManagerApprovalGateError(caught)) {
        onGateInvalid();
        return;
      }
      if ((caught as { problem?: { title?: string } }).problem?.title === "GIFT_CARD_REPLACEMENT_REQUIRED")
        setReplacementFor(selectedRequest.id);
      setError(valueError(caught, language));
      await loadRequests();
    } finally {
      setBusy(false);
    }
  };

  const retryExecution = async () => {
    if (!selectedRequest) return;

    try {
      setBusy(true);
      const replacement = replacementFor === selectedRequest.id ? giftReplacementInput(noExpiry, expiry, language) : undefined;
      const executed = await executeApprovalRequest(selectedRequest, managerAccessToken, replacement);
      setReplacementFor(null);
      setSelectedRequest(executed);
      toast.notify({
        tone: "success",
        title: ar ? "تم تنفيذ العملية المعتمدة" : "Approved action executed"
      });
      await loadRequests();
    } catch (caught) {
      if (isManagerApprovalGateError(caught)) {
        onGateInvalid();
        return;
      }
      if ((caught as { problem?: { title?: string } }).problem?.title === "GIFT_CARD_REPLACEMENT_REQUIRED")
        setReplacementFor(selectedRequest.id);
      setError(valueError(caught, language));
      await loadRequests();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      <PageHeader
        eyebrow={ar ? "رقابة العمليات" : "Operational control"}
        title={ar ? "موافقات المدير" : "Manager Approvals"}
        description={
          ar
            ? `${context.branchName} · راجع طلبات الإهلاك والاسترجاع وإعادة جدولة أو إلغاء الطلبات المستقبلية ضمن ضوابط المدير.`
            : `${context.branchName} · Review void, refund, future-order reschedule and cancellation requests under controlled manager authorization.`
        }
        actions={
          <Inline>
            <StatusBadge tone="success">
              {ar ? "PIN موثّق من الخادم" : "Server PIN verified"}
            </StatusBadge>
            <StatusBadge tone={canDecide ? "success" : "warning"}>
              {canDecide
                ? ar ? "صلاحية الاعتماد فعالة" : "Decision access active"
                : ar ? "عرض فقط" : "Read only"}
            </StatusBadge>
            {canSelfDecide ? (
              <StatusBadge tone="warning">
                {ar ? "استثناء المالك مسجّل" : "Owner override audited"}
              </StatusBadge>
            ) : null}
            <StatusBadge tone={pendingCount ? "warning" : "success"}>
              {ar ? `${pendingCount} معلّق` : `${pendingCount} pending`}
            </StatusBadge>
            <Button variant="secondary" disabled={loading || busy} onClick={() => void loadRequests(true)}>
              {ar ? "تحديث الآن" : "Refresh now"}
            </Button>
          </Inline>
        }
      />

      <Alert tone="info" title={ar ? "دخول مدير موثّق بالرمز السري" : "Server-verified manager PIN access"}>
        {ar
          ? canSelfDecide
            ? `تحقق الخادم من رمز المدير لهذا الحساب. تظل هذه التذكرة مرتبطة بالمستخدم والفرع والدور والجلسة حتى ${new Date(managerAccessExpiresAt).toLocaleTimeString("ar-AE")}. حساب المالك التجريبي يملك استثناء موافقة ذاتية مقيّداً ببيئة UAT، ويتطلب سبباً ويسجل بالكامل في سجل التدقيق.`
            : `تحقق الخادم من رمز المدير لهذا الحساب. تظل هذه التذكرة مرتبطة بالمستخدم والفرع والدور والجلسة حتى ${new Date(managerAccessExpiresAt).toLocaleTimeString("ar-AE")}. تبقى الموافقة الذاتية ممنوعة، ويجب أن يكون مقدم الطلب والموافق شخصين مختلفين.`
          : canSelfDecide
            ? `The server verified this manager PIN. The short-lived gate is bound to the current user, branch, role and session until ${new Date(managerAccessExpiresAt).toLocaleTimeString("en-AE")}. The Demo Owner has a UAT-scoped self-decision override; a reason is required and the override is fully audited.`
            : `The server verified this manager PIN. The short-lived gate is bound to the current user, branch, role and session until ${new Date(managerAccessExpiresAt).toLocaleTimeString("en-AE")}. Self-approval remains blocked and requester and approver must be different users.`}
      </Alert>

      {error ? (
        <Alert tone="danger" title={ar ? "تعذر تنفيذ العملية" : "Action failed"} onDismiss={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Grid columns={4}>
        <KpiCard label={ar ? "بانتظار الموافقة" : "Awaiting approval"} value={pendingCount} trendTone={pendingCount ? "warning" : "success"} />
        <KpiCard label={ar ? "تم تنفيذها" : "Executed"} value={executedCount} trendTone="success" />
        <KpiCard label={ar ? "تحتاج إعادة محاولة" : "Needs retry"} value={executionFailedCount} trendTone={executionFailedCount ? "danger" : "success"} />
        <KpiCard label={ar ? "إجمالي السجل" : "Total audit queue"} value={requests.length} />
      </Grid>

      <Panel
        title={ar ? "قائمة Pending Approvals" : "Pending Approvals Queue"}
        description={
          ar
            ? `تتحدث تلقائيًا كل 10 ثوانٍ${lastUpdatedAt ? ` · آخر تحديث ${lastUpdatedAt.toLocaleTimeString("ar-AE")}` : ""}.`
            : `Automatically refreshes every 10 seconds${lastUpdatedAt ? ` · Last updated ${lastUpdatedAt.toLocaleTimeString("en-AE")}` : ""}.`
        }
      >
        <Stack>
          <Inline>
            <SelectField
              label={ar ? "الحالة" : "Status"}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            >
              <option value="Pending">{ar ? "المعلقة فقط" : "Pending only"}</option>
              <option value="All">{ar ? "جميع الحالات" : "All statuses"}</option>
              <option value="Approved">{ar ? "موافق عليها" : "Approved"}</option>
              <option value="Rejected">{ar ? "مرفوضة" : "Rejected"}</option>
              <option value="Expired">{ar ? "منتهية" : "Expired"}</option>
            </SelectField>
          </Inline>

          {loading ? (
            <Card>
              <Inline>
                <Spinner label={ar ? "تحميل الطلبات" : "Loading requests"} />
                <span>{ar ? "جارٍ تحميل طلبات المدير…" : "Loading manager requests…"}</span>
              </Inline>
            </Card>
          ) : (
            <DataTable
              rows={filteredRequests}
              rowKey={(row) => row.id}
              emptyTitle={ar ? "لا توجد طلبات مطابقة" : "No matching approval requests"}
              columns={[
                {
                  key: "operation",
                  header: ar ? "العملية" : "Operation",
                  cell: (row) => (
                    <span>
                      <strong>{operationLabel(row)}</strong>
                      <small className="manager-approvals-subtext">{row.branchName}</small>
                    </span>
                  )
                },
                {
                  key: "order",
                  header: ar ? "الطلب والمبلغ" : "Order & amount",
                  cell: (row) => {
                    const payload = readOperationalPayload(row);
                    const amount = approvalDisplayAmount(row, payload);
                    return (
                      <span>
                        <strong>{approvalDisplayReference(payload)}</strong>
                        <small className="manager-approvals-subtext">
                          {amount == null ? "—" : formatMoney(amount, payload?.currency)}
                        </small>
                      </span>
                    );
                  }
                },
                {
                  key: "requester",
                  header: ar ? "مقدم الطلب" : "Requester",
                  cell: (row) => (
                    <span>
                      <strong>{row.requestedByName}</strong>
                      <small className="manager-approvals-subtext">
                        {new Date(row.requestedAt).toLocaleString(ar ? "ar-AE" : "en-AE")}
                      </small>
                    </span>
                  )
                },
                {
                  key: "decision",
                  header: ar ? "القرار" : "Decision",
                  cell: (row) => (
                    <ApprovalBadge
                      state={
                        row.status === "Approved"
                          ? "Approved"
                          : row.status === "Rejected"
                            ? "Rejected"
                            : row.status === "Pending"
                              ? "Pending"
                              : "NotRequired"
                      }
                    />
                  )
                },
                {
                  key: "execution",
                  header: ar ? "التنفيذ" : "Execution",
                  cell: executionBadge
                },
                {
                  key: "action",
                  header: "",
                  cell: (row) => (
                    <Button size="sm" variant={row.status === "Pending" ? "primary" : "secondary"} onClick={() => openRequest(row)}>
                      {row.status === "Pending" ? (ar ? "مراجعة واعتماد" : "Review & decide") : (ar ? "عرض" : "View")}
                    </Button>
                  )
                }
              ]}
            />
          )}
        </Stack>
      </Panel>

      <Dialog
        open={Boolean(selectedRequest)}
        onClose={() => {
          if (!busy) setSelectedRequest(null);
        }}
        title={selectedRequest ? `${operationLabel(selectedRequest)} · ${approvalDisplayReference(selectedPayload)}` : ""}
        footer={
          selectedRequest ? (
            <Inline>
              <Button variant="secondary" disabled={busy} onClick={() => setSelectedRequest(null)}>
                {ar ? "إغلاق" : "Close"}
              </Button>
              {canDecide && selectedRequest.status === "Pending" ? (
                <>
                  <Button variant="danger" disabled={busy || !decisionReason.trim()} onClick={() => void reject()}>
                    {ar ? "رفض الطلب" : "Reject"}
                  </Button>
                  <Button disabled={busy} onClick={() => void approveAndExecute()}>
                    {ar ? "موافقة وتنفيذ" : "Approve & execute"}
                  </Button>
                </>
              ) : null}
              {canDecide && selectedRequest.status === "Approved" && selectedRequest.executionStatus !== "Succeeded" ? (
                <Button disabled={busy || (replacementFor === selectedRequest.id && (!canSetExpiry || (!noExpiry && !expiry)))} onClick={() => void retryExecution()}>
                  {replacementFor === selectedRequest.id ? (ar ? "إصدار بطاقة بديلة وإتمام الاسترداد" : "Issue replacement card and complete refund") : selectedRequest.executionStatus === "Failed"
                    ? ar ? "إعادة محاولة التنفيذ" : "Retry execution"
                    : ar ? "تنفيذ العملية" : "Execute action"}
                </Button>
              ) : null}
            </Inline>
          ) : null
        }
      >
        {selectedRequest ? (
          <Stack>
            {replacementFor === selectedRequest.id && selectedRequest.executionStatus !== "Succeeded" ? <GiftCardReplacementFields language={language} permitted={canSetExpiry} noExpiry={noExpiry} expiry={expiry} onNoExpiry={setNoExpiry} onExpiry={setExpiry} disabled={busy} /> : null}
            {selectedPayload && selectedRequest.executionStatus === "Succeeded" && selectedRequest.executionReferenceId && (selectedRequest.operation === "Refund" || selectedRequest.operation === "PreOrderCancellation") ? <RefundGiftCardPrintPanel key={selectedRequest.id} language={language} orderId={selectedPayload.orderId} refundId={selectedRequest.operation === "Refund" ? selectedRequest.executionReferenceId : undefined} settlementId={selectedRequest.operation === "PreOrderCancellation" ? selectedRequest.executionReferenceId : undefined} /> : null}
            <div className="manager-approvals-header">
              <div>
                <small>{ar ? "القرار" : "Decision"}</small>
                <ApprovalBadge
                  state={
                    selectedRequest.status === "Approved"
                      ? "Approved"
                      : selectedRequest.status === "Rejected"
                        ? "Rejected"
                        : selectedRequest.status === "Pending"
                          ? "Pending"
                          : "NotRequired"
                  }
                />
              </div>
              <div>
                <small>{ar ? "التنفيذ" : "Execution"}</small>
                {executionBadge(selectedRequest)}
              </div>
              <div>
                <small>{ar ? "الفرع" : "Branch"}</small>
                <strong>{selectedRequest.branchName}</strong>
              </div>
            </div>

            {!canDecide && selectedRequest.status === "Pending" ? (
              <Alert tone="warning" title={ar ? "عرض فقط" : "Read-only access"}>
                {ar
                  ? "يتطلب اتخاذ القرار صلاحية approvals.decide."
                  : "Approving or rejecting requires the approvals.decide permission."}
              </Alert>
            ) : null}

            {selectedRequest.executionError ? (
              <Alert tone="danger" title={ar ? "فشل آخر تنفيذ" : "Last execution failed"}>
                {selectedRequest.executionError}
              </Alert>
            ) : null}

            <Grid columns={2}>
              <Card title={ar ? "بيانات الطلب" : "Order details"}>
                <Stack>
                  <span><strong>{ar ? "رقم الطلب" : "Order"}</strong><small className="manager-approvals-subtext">{selectedPayload?.orderNumber ?? "—"}</small></span>
                  {selectedPayload?.bookingNumber ? (
                    <span><strong>{ar ? "رقم الحجز" : "Booking"}</strong><small className="manager-approvals-subtext">{selectedPayload.bookingNumber}</small></span>
                  ) : null}
                  <span><strong>{ar ? "مقدم الطلب" : "Requested by"}</strong><small className="manager-approvals-subtext">{selectedRequest.requestedByName}</small></span>
                  <span><strong>{ar ? "وقت الطلب" : "Requested at"}</strong><small className="manager-approvals-subtext">{new Date(selectedRequest.requestedAt).toLocaleString(ar ? "ar-AE" : "en-AE")}</small></span>
                  <span><strong>{ar ? "ينتهي في" : "Expires at"}</strong><small className="manager-approvals-subtext">{new Date(selectedRequest.expiresAt).toLocaleString(ar ? "ar-AE" : "en-AE")}</small></span>
                </Stack>
              </Card>
              <Card title={ar ? "الأثر المالي والتشغيلي" : "Financial & operational impact"}>
                <Stack>
                  {selectedRequest.operation === "Refund" ? (
                    <span><strong>{ar ? "قيمة الاسترجاع" : "Refund amount"}</strong><small className="manager-approvals-subtext manager-approvals-amount">{formatMoney(selectedPayload?.requestedRefundTotalMinor, selectedPayload?.currency)}</small></span>
                  ) : null}
                  {selectedRequest.operation === "VoidItem" ? (
                    <span><strong>{ar ? "قيمة الطلب" : "Order value"}</strong><small className="manager-approvals-subtext manager-approvals-amount">{formatMoney(selectedPayload?.orderTotalMinor, selectedPayload?.currency)}</small></span>
                  ) : null}
                  {selectedRequest.operation === "PreOrderReschedule" ? (
                    <>
                      <span><strong>{ar ? "المقبوضات المرتبطة" : "Captured payments"}</strong><small className="manager-approvals-subtext manager-approvals-amount">{formatMoney((selectedPayload?.depositPaidMinor ?? 0) + (selectedPayload?.balancePaidMinor ?? 0), selectedPayload?.currency)}</small></span>
                      <span><strong>{ar ? "الموعد الحالي" : "Current fulfillment"}</strong><small className="manager-approvals-subtext">{selectedPayload?.previousFulfillmentAt ? new Date(selectedPayload.previousFulfillmentAt).toLocaleString(ar ? "ar-AE" : "en-AE") : "—"}</small></span>
                      <span><strong>{ar ? "الموعد المطلوب" : "Requested fulfillment"}</strong><small className="manager-approvals-subtext">{selectedPayload?.fulfillmentAt ? new Date(selectedPayload.fulfillmentAt).toLocaleString(ar ? "ar-AE" : "en-AE") : "—"}</small></span>
                      <span><strong>{ar ? "مهلة التحضير" : "Preparation lead"}</strong><small className="manager-approvals-subtext">{selectedPayload?.previousPreparationLeadMinutes ?? "—"} → {selectedPayload?.preparationLeadMinutes ?? "—"} {ar ? "دقيقة" : "minutes"}</small></span>
                    </>
                  ) : null}
                  {selectedRequest.operation === "PreOrderCancellation" ? (
                    <>
                      <span><strong>{ar ? "العربون المقبوض" : "Captured deposit"}</strong><small className="manager-approvals-subtext">{formatMoney(selectedPayload?.depositCapturedMinor, selectedPayload?.currency)}</small></span>
                      <span><strong>{ar ? "الرصيد المقبوض" : "Captured balance"}</strong><small className="manager-approvals-subtext">{formatMoney(selectedPayload?.balanceCapturedMinor, selectedPayload?.currency)}</small></span>
                      <span><strong>{ar ? "تسوية العربون" : "Deposit disposition"}</strong><small className="manager-approvals-subtext">{cancellationDispositionLabel(selectedPayload?.disposition)}</small></span>
                      <span><strong>{ar ? "إجمالي الاسترداد المتوقع" : "Expected refund"}</strong><small className="manager-approvals-subtext manager-approvals-amount">{formatMoney(cancellationRefundExposure(selectedPayload), selectedPayload?.currency)}</small></span>
                    </>
                  ) : null}
                  {selectedPayload?.originalTotalMinor != null ? (
                    <span><strong>{ar ? "إجمالي الفاتورة الأصلية" : "Original sale total"}</strong><small className="manager-approvals-subtext">{formatMoney(selectedPayload.originalTotalMinor, selectedPayload.currency)}</small></span>
                  ) : null}
                  <span><strong>{ar ? "السبب" : "Reason"}</strong><small className="manager-approvals-subtext">{selectedRequest.requestReason ?? selectedPayload?.reason ?? "—"}</small></span>
                </Stack>
              </Card>
            </Grid>

            {selectedPayload?.lines?.length ? (
              <Card title={ar ? "البنود المطلوب استرجاعها" : "Requested refund lines"}>
                <div className="manager-approvals-lines">
                  {selectedPayload.lines.map((line) => (
                    <div key={line.originalOrderLineId} className="manager-approvals-line">
                      <span>
                        <strong>{ar ? line.productNameAr : line.productNameEn}</strong>
                        <small>{ar ? line.variantNameAr : line.variantNameEn} · {line.sku}</small>
                      </span>
                      <span>
                        <strong>{line.quantityMilli / 1000}</strong>
                        <small>{ar ? "الكمية" : "Qty"}</small>
                      </span>
                      <strong>{formatMoney(line.totalMinor, selectedPayload.currency)}</strong>
                    </div>
                  ))}
                </div>
              </Card>
            ) : null}

            {selectedRequest.operation === "VoidItem" ? (
              <Alert tone="warning" title={ar ? "إهلاك طلب جاهز" : "Ready-order void"}>
                {ar
                  ? `سيتم نقل الطلب من ${selectedPayload?.orderStatus ?? "Ready"} إلى Cancelled وإلغاء آثار المطبخ والطاولة دون إنشاء استرجاع مالي.`
                  : `The order will move from ${selectedPayload?.orderStatus ?? "Ready"} to Cancelled and its kitchen/table artifacts will be released without creating a financial refund.`}
              </Alert>
            ) : null}

            {selectedRequest.operation === "PreOrderReschedule" ? (
              <Alert tone="info" title={ar ? "أثر إعادة الجدولة" : "Reschedule impact"}>
                {ar
                  ? `سيُعاد حساب موعد الإرسال للمطبخ. إذا كان الطلب قد أُرسل، ستُلغى تذاكر التحضير الحالية ويُعاد إدراجه تلقائياً عند الموعد الجديد دون تغيير الدفعات الأصلية.`
                  : `The kitchen release horizon will be recalculated. If already released, current preparation tickets are cancelled and the order is queued for the new time without changing original payments.`}
              </Alert>
            ) : null}

            {selectedRequest.operation === "PreOrderCancellation" ? (
              <Alert tone="warning" title={ar ? "تسوية إلغاء محمية" : "Protected cancellation settlement"}>
                {ar
                  ? `سيبقى سجل الدفعات الأصلي دون تعديل. تُسجّل الاستردادات كحركات مقابلة في وردية التنفيذ، بينما تُسجّل المصادرة مستقلة ولا تُعامل كمبيعات مكتملة أو مرتجعات VAT.`
                  : `Original payment records remain immutable. Refunds are posted as counter-entries in the execution shift, while forfeiture is recorded separately and is not treated as completed sales or VAT refunds.`}
              </Alert>
            ) : null}

            {selectedRequest.status === "Pending" ? (
              <TextAreaField
                label={ar ? "ملاحظة قرار المدير" : "Manager decision note"}
                value={decisionReason}
                onChange={(event) => setDecisionReason(event.target.value)}
                placeholder={
                  ar
                    ? "سبب الرفض إلزامي، وملاحظة الموافقة اختيارية."
                    : "A rejection reason is required; an approval note is optional."
                }
              />
            ) : null}

            {selectedRequest.executionStatus === "Succeeded" ? (
              <Alert tone="success" title={ar ? "تم التنفيذ والتوثيق" : "Executed and recorded"}>
                {ar
                  ? `نفّذها ${selectedRequest.executedByName ?? "المدير"}. المرجع: ${selectedRequest.executionReferenceType ?? "—"} / ${selectedRequest.executionReferenceId ?? "—"}.`
                  : `Executed by ${selectedRequest.executedByName ?? "manager"}. Reference: ${selectedRequest.executionReferenceType ?? "—"} / ${selectedRequest.executionReferenceId ?? "—"}.`}
              </Alert>
            ) : null}
          </Stack>
        ) : null}
      </Dialog>
    </Stack>
  );
}
